import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faTag, faXmark } from '@fortawesome/free-solid-svg-icons';
import { cn } from '../../../../ui/utils/cn';
import { Input } from '../../../../ui';
import { searchBrands } from '../../api/coffeeApi';
import { CoffeeBrand } from '../../CoffeeCardWeb/types';

// What the picker emits upward: either an existing brand (brandRef + its name for
// display) or a brand-new name to create-or-get at submit time. Mirrors the
// supermarket create body (brandRef? | brandName?). Creation is deferred — the
// supermarket endpoint create-or-gets the brand, so we never call createBrand here.
export interface BrandPickerValue {
    brandRef?: string;
    brandName?: string;
}

interface BrandPickerProps {
    value: BrandPickerValue | null;
    onChange: (next: BrandPickerValue | null) => void;
    disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;

const BrandPicker: React.FC<BrandPickerProps> = ({ value, onChange, disabled = false }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<CoffeeBrand[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const hasSelection = !!value && (!!value.brandRef || !!value.brandName);
    const selectedLabel = value?.brandName || '';

    // debounced autocomplete: only fires once the user has typed a non-empty
    // query AND nothing is selected — so there is no network call on mount.
    useEffect(() => {
        if (hasSelection) return;
        const trimmed = query.trim();
        if (!trimmed) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        let cancelled = false;
        setIsSearching(true);
        const handle = setTimeout(async () => {
            try {
                const brands = await searchBrands(trimmed);
                if (!cancelled) setResults(brands);
            } catch {
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [query, hasSelection]);

    const trimmed = query.trim();
    const hasExactMatch = results.some(
        (b) => b.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );

    const selectExisting = (brand: CoffeeBrand) => {
        onChange({ brandRef: brand._id, brandName: brand.name });
        setQuery('');
        setResults([]);
    };

    const addNew = () => {
        if (!trimmed) return;
        onChange({ brandName: trimmed });
        setQuery('');
        setResults([]);
    };

    const clearSelection = () => {
        onChange(null);
        setQuery('');
        setResults([]);
    };

    // selected state: a removable pill, no search UI until cleared.
    if (hasSelection) {
        return (
            <div className="flex items-center justify-between gap-2 p-3 border border-mint/50 bg-mint/10 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                    <FontAwesomeIcon icon={faTag} className="h-3.5 w-3.5 text-mint shrink-0" />
                    <span className="font-medium text-sm truncate">{selectedLabel}</span>
                    {!value?.brandRef && (
                        <span className="text-xs bg-mint/20 text-mint px-1.5 py-0.5 rounded shrink-0">new</span>
                    )}
                </div>
                {!disabled && (
                    <button
                        type="button"
                        onClick={clearSelection}
                        className="text-muted-foreground hover:text-ink transition-colors shrink-0"
                        aria-label="Clear selected brand"
                    >
                        <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <Input
                placeholder="Search a coffee brand…"
                value={query}
                disabled={disabled}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 text-sm"
            />

            {trimmed && (
                <div className="space-y-2 border border-ink/10 bg-muted/10 rounded-xl p-2">
                    {isSearching && (
                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
                            <FontAwesomeIcon icon={faSearch} className="h-3 w-3 animate-spin" />
                            Searching brands…
                        </div>
                    )}

                    {!isSearching && results.map((brand) => (
                        <button
                            key={brand._id}
                            type="button"
                            onClick={() => selectExisting(brand)}
                            className={cn(
                                'w-full text-left p-2.5 border border-ink/10 bg-background rounded-lg cursor-pointer transition-all',
                                'hover:shadow-md hover:border-mint/40 hover:bg-mint/5 flex items-center justify-between gap-2',
                            )}
                        >
                            <span className="font-medium text-sm truncate flex items-center gap-2">
                                {brand.color && (
                                    <span
                                        className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                                        style={{ backgroundColor: brand.color }}
                                    />
                                )}
                                {brand.name}
                            </span>
                            {brand.ratingCount > 0 && (
                                <span className="text-xs text-muted-foreground shrink-0">
                                    {brand.ratingCount} rated
                                </span>
                            )}
                        </button>
                    ))}

                    {/* add-new affordance — only when the typed text matches no existing brand */}
                    {!isSearching && !hasExactMatch && (
                        <button
                            type="button"
                            onClick={addNew}
                            className="w-full p-2.5 border border-dashed border-ink/20 bg-muted/10 rounded-lg cursor-pointer transition-all hover:border-mint/40 hover:bg-mint/5 flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">
                                Add &ldquo;{trimmed}&rdquo;
                            </span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BrandPicker;
